'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getInstanceClient } from '../../../../di/entry.browser.js';
import { drawQueryKeys, sportQueryKeys } from '../../../../lib/entry.js';
import { mapDrawEventOddsToSportEventOdds, mapDrawEventToSportEvent } from '../../lib/entry.js';
/**
 * Hook to get subscribed draw.
 *
 * @param drawQueryOptions Draw query argument
 * @param drawQueryOptions.product - The product for which to get the draw.
 * @param drawQueryOptions.drawNumber - The draw number to get the draw for.
 * @param options Options object
 * @param options.skip conditionally skip subscribe
 * @param options.syncSportEvents sync derived sport event caches from draw updates
 */
export function useSubscribedDraw(t0, options) {
  const $ = _c(8);
  const {
    product,
    drawNumber
  } = t0;
  const queryClient = useQueryClient();
  const isSkipped = options?.skip ?? false;
  const shouldSyncSportEvents = options?.syncSportEvents ?? true;
  const selectData = options?.selectData;
  let t1;
  let t2;
  if ($[0] !== drawNumber || $[1] !== isSkipped || $[2] !== product || $[3] !== queryClient || $[4] !== selectData || $[5] !== shouldSyncSportEvents) {
    t1 = () => {
      if (isSkipped) {
        return _temp;
      }
      const controller = getInstanceClient("ISubscribeDrawController");
      const queryKeys = drawQueryKeys.draw(product, drawNumber);
      const unsubscribedDrawForecast = controller({
        product,
        drawNumber
      }, nextData => {
        const queryData = selectData?.(nextData) ?? nextData;
        queryClient.setQueryData(queryKeys, () => queryData);
        if (!shouldSyncSportEvents) {
          return;
        }
        for (const eventObject of nextData.draw.events) {
          if (eventObject.status === "MINIMAL") {
            continue;
          }
          const {
            eventId
          } = eventObject;
          const sportEventQueryKey = sportQueryKeys.sportEvent(eventId);
          const bettingEventId = `${eventId}_Team`;
          const bettingEvent = nextData.bettingEvent[bettingEventId];
          if (!bettingEvent) {
            continue;
          }
          const sportEventData = mapDrawEventToSportEvent(bettingEvent, nextData);
          if (!sportEventData) {
            continue;
          }
          queryClient.setQueryData(sportEventQueryKey, () => sportEventData);
          const sportEventOddsData = mapDrawEventOddsToSportEventOdds(bettingEvent, nextData);
          if (!sportEventOddsData) {
            continue;
          }
          const sportEventOddsQueryKey = sportQueryKeys.sportEventOdds(eventId);
          queryClient.setQueryData(sportEventOddsQueryKey, () => sportEventOddsData);
        }
      });
      return unsubscribedDrawForecast;
    };
    t2 = [product, drawNumber, queryClient, isSkipped, shouldSyncSportEvents, selectData];
    $[0] = drawNumber;
    $[1] = isSkipped;
    $[2] = product;
    $[3] = queryClient;
    $[4] = selectData;
    $[5] = shouldSyncSportEvents;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
}
function _temp() {}